import React, { useRef } from 'react';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { Button } from '../Button';
import { Modal } from './Modal';
import { cn } from '@/utils/cn';
import type { ModalProps } from './Modal.types';

export interface ConfirmationModalProps extends Omit<ModalProps, 'children' | 'title'> {
  title: string;
  description?: React.ReactNode;

  /**
   * Typ potvrzeni (ovlivnuje barvu a ikonu).
   * @default 'danger'
   */
  intent?: 'danger' | 'warning' | 'info' | 'success';

  /**
   * Text potvrzovaciho tlacitka.
   * @default 'Potvrdit'
   */
  confirmText?: string;

  /**
   * Text zrusovaciho tlacitka.
   * @default 'Zrušit'
   */
  cancelText?: string;

  /**
   * Callback pri potvrzeni.
   */
  onConfirm: () => void;

  /**
   * Zda je operace asynchronni (loading stav).
   */
  isLoading?: boolean;
}

/**
 * # ConfirmationModal
 *
 * Predpripraveny modal pro potvrzovani akci.
 *
 * @example
 * <ConfirmationModal
 *   isOpen={isOpen}
 *   onClose={close}
 *   onConfirm={deleteItem}
 *   title="Smazat polozku?"
 *   intent="danger"
 * />
 */
export const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  intent = 'danger',
  confirmText = 'Potvrdit',
  cancelText = 'Zrušit',
  isLoading = false,
  size = 'md',
  ...props
}: ConfirmationModalProps) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const intentStyles = {
    danger: {
      icon: <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-500" />,
      bg: 'bg-red-100 dark:bg-red-900/20',
      btnVariant: 'danger' as const,
    },
    warning: {
      icon: <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-500" />,
      bg: 'bg-yellow-100 dark:bg-yellow-900/20',
      btnVariant: 'secondary' as const, // Warning doesn't have a specific button variant usually, maybe primary or secondary
    },
    info: {
      icon: <Info className="h-6 w-6 text-cyan-600 dark:text-cyan-500" />,
      bg: 'bg-cyan-100 dark:bg-cyan-900/20',
      btnVariant: 'primary' as const,
    },
    success: {
      icon: <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-500" />,
      bg: 'bg-green-100 dark:bg-green-900/20',
      btnVariant: 'success' as const,
    },
  }[intent];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size} initialFocus={cancelButtonRef} {...props}>
      <div className="px-6 py-6 pb-0">
        <div className="sm:flex sm:items-start">
          <div
            className={cn(
              'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
              intentStyles.bg,
            )}
          >
            {intentStyles.icon}
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
            <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
              {title}
            </h3>
            {description && (
              <div className="mt-2">
                <div className="text-sm text-gray-500 dark:text-zinc-400">{description}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal.Footer className="mt-6 border-t-0 bg-transparent pt-4 pb-6 sm:pb-6">
        <Button variant={intentStyles.btnVariant} onClick={onConfirm} loading={isLoading}>
          {confirmText}
        </Button>
        <Button variant="outline" onClick={onClose} ref={cancelButtonRef} disabled={isLoading}>
          {cancelText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
